import React, { useState } from 'react';
import axios from 'axios';
import { MapPin, Mail, Phone, Send, CheckCircle2 } from 'lucide-react';
import { contactInfo } from '../data/mock';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Please fill in all fields', description: 'We need your name, email and a message to reach back.' });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast({ title: 'Message sent!', description: 'Our team will get back to you within 24 hours.' });
      setTimeout(() => {
        setForm({ name: '', email: '', message: '' });
        setSent(false);
      }, 2500);
    } catch (err) {
      toast({ title: 'Could not send', description: err?.response?.data?.detail || 'Please try again shortly.' });
    } finally {
      setLoading(false);
    }
  };

  const items = [
    { icon: MapPin, title: 'Our Office', value: contactInfo.office, sub: 'Come say dumela!' },
    { icon: Mail, title: 'Email us', value: contactInfo.email, sub: 'We reply within 24 hours', href: `mailto:${contactInfo.email}` },
    { icon: Phone, title: 'Call us', value: contactInfo.phone, sub: '24/7 live support', href: `tel:${contactInfo.phone.replace(/\s/g, '')}` },
  ];

  return (
    <section id="contact" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-block px-3 py-1 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold tracking-wide uppercase mb-4">
            Contact us
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-[#0b2f5c] tracking-tight mb-4">
            Let’s talk
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Need help? Have a question? Our support team is ready to assist you anytime.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {items.map((it) => {
              const Icon = it.icon;
              const Wrap = it.href ? 'a' : 'div';
              return (
                <Wrap
                  key={it.title}
                  href={it.href}
                  className="group flex items-start gap-4 bg-white rounded-2xl p-5 border border-slate-200 hover:border-sky-300 hover:shadow-md transition-all"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-100 to-sky-50 flex items-center justify-center flex-shrink-0 group-hover:from-sky-400 group-hover:to-sky-600 transition-all">
                    <Icon className="w-5 h-5 text-sky-500 group-hover:text-white transition-colors" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-1">{it.title}</div>
                    <div className="text-[#0b2f5c] font-bold text-base mb-0.5">{it.value}</div>
                    <div className="text-xs text-slate-500">{it.sub}</div>
                  </div>
                </Wrap>
              );
            })}
          </div>

          <div className="lg:col-span-3">
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-3xl p-8 border border-slate-200 shadow-sm"
            >
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Your name</label>
                  <Input
                    placeholder="Kagiso Mokoena"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="border-slate-200 focus-visible:ring-sky-400 h-11"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Email</label>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="border-slate-200 focus-visible:ring-sky-400 h-11"
                  />
                </div>
              </div>
              <div className="mb-5">
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Message</label>
                <Textarea
                  rows={5}
                  placeholder="How can we help?"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="border-slate-200 focus-visible:ring-sky-400 resize-none"
                />
              </div>
              <Button
                type="submit"
                disabled={sent || loading}
                className="w-full bg-[#0b2f5c] hover:bg-[#0f3a70] text-white h-12 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all"
              >
                {sent ? (
                  <>
                    <CheckCircle2 className="w-5 h-5 mr-2" /> Message sent
                  </>
                ) : loading ? (
                  'Sending…'
                ) : (
                  <>
                    Send message <Send className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
